import { jsx, jsxs } from "react/jsx-runtime";
import * as React from "react";
import { useRef, useMemo, useEffect } from "react";
import { ErrorBoundary } from "/app/node_modules/.pnpm/react-error-boundary@3.1.4_react@18.2.0/node_modules/react-error-boundary/dist/react-error-boundary.esm.js";
import { GlobalBorderStyles } from "../../common/UI/Borders.js";
import { ErrorFallback } from "../../common/UI/ErrorFallback.js";
import { ColumnsProvider } from "../../context/useColumnsContext.js";
import { CompleteThemeProvider } from "../../context/useCompleteTheme.js";
import { FindAndReplaceProvider } from "../../context/useFindAndReplaceContext.js";
import { KeyActionsProvider } from "../../context/useKeyActions.js";
import { SearchValueProvider } from "../../context/useSearchValueContext.js";
import { named } from "../../utils/named.js";
import { setUseWhatChange } from "@simbathesailor/use-what-changed";
import { useBulkRowSelection } from "../../context/useBulkRowSelection.js";
import { ColumnSelectionGroupProvider } from "../../context/useColumnSelectionGroupContext.js";
import { EditorProvider } from "../../context/useEditorContext.js";
import { EditorValueProvider } from "../../context/useEditorValueContext.js";
import { ClipBoardActionsProvider } from "../../context/useClipboardActions.js";
import TablePortal from "./TablePortal.js";
import { VirtualizedTable } from "./VirtualizedTable.js";
import { DEFAULT_ROW_HEIGHT, TABLE_ROW_LIMIT, TABLE_HIDDEN_ROWS_AT_LIMIT } from "./constants.js";
setUseWhatChange(true);
const MemoizedTable = React.memo(VirtualizedTable);
const getTotalRowCount = (totalRows, totalColumns) => {
  return totalColumns ? totalRows : 0;
};
const getAdditionalRowCount = (totalRows, totalColumns, additionalRows, readOnly) => {
  if (!totalColumns)
    return 0;
  if (totalRows > TABLE_ROW_LIMIT) {
    const difference = TABLE_ROW_LIMIT + TABLE_HIDDEN_ROWS_AT_LIMIT - totalRows;
    return difference < 0 ? 0 : difference;
  }
  if (readOnly)
    return 0;
  return additionalRows ?? 0;
};
const Table = ({
  additionalRows,
  cellClassName,
  columnDiff,
  columnConfig,
  columnContextMenuSlot,
  columnSelectionEnabled,
  decorateCell,
  decorateFooterCell,
  disableColumnMenus,
  emptyState,
  getFindAndReplaceModalProps,
  height,
  innerTableRef,
  isDiffView,
  isFiltered,
  isLoading,
  onActionSuccess,
  onCellClick,
  onDownloadTable,
  onFindAndReplace,
  onScroll,
  onSearchValue,
  onSelectedRows,
  onSelectionChange,
  onSort,
  pinningEnabled,
  readOnly,
  rowSelectionType,
  selectedRows,
  setTableIsEditing,
  showColumnMenus,
  sortOverride,
  tableId,
  theme,
  totalRowCount,
  useFilterByValue,
  width,
  columnMetaComponent,
  canAddRows,
  canInsertRows,
  cellContextMenuSlot,
  toolMenuSlot,
  headerInjections
}) => {
  const { setSelectionAnchorIndex } = useBulkRowSelection();
  const totalColumns = columnConfig.length;
  const totalRows = getTotalRowCount(totalRowCount, totalColumns);
  const additional = getAdditionalRowCount(
    totalRows,
    totalColumns,
    additionalRows,
    readOnly
  );
  const tableRef = useRef(null);
  const rowHeight = (theme == null ? void 0 : theme.rowHeight) ?? DEFAULT_ROW_HEIGHT;
  const table = useMemo(
    () => /* @__PURE__ */ jsx(
      MemoizedTable,
      {
        additionalRows: additional,
        cellClassName,
        emptyState,
        height,
        onFindAndReplace,
        onSearchValue,
        onSelectedRows,
        onSelectionChange,
        onSort,
        onCellClick,
        readOnly,
        selectedRows,
        sortOverride,
        innerTableRef,
        totalRowCount: totalRows,
        isDiffView,
        rowSelectionType,
        pinningEnabled,
        columnSelectionEnabled,
        showColumnMenus,
        width,
        tableId,
        tableRef,
        useFilterByValue,
        setTableIsEditing,
        onScroll,
        isLoading,
        decorateCell,
        decorateFooterCell,
        columnContextMenuSlot,
        columnMetaComponent,
        rowHeight,
        canAddRows,
        canInsertRows,
        cellContextMenuSlot,
        toolMenuSlot,
        headerInjections
      }
    ),
    [
      totalRows,
      totalColumns,
      selectedRows,
      additional,
      readOnly,
      emptyState,
      onScroll,
      isLoading,
      decorateCell,
      columnContextMenuSlot,
      columnMetaComponent,
      rowHeight,
      toolMenuSlot,
      headerInjections
    ]
  );
  useEffect(() => {
    const tableKeyUpHandler = (event) => {
      if (!event.shiftKey) {
        setSelectionAnchorIndex(null);
      }
    };
    document.body.addEventListener("keyup", tableKeyUpHandler);
    return () => {
      document.body.removeEventListener("keyup", tableKeyUpHandler);
    };
  }, [totalRows]);
  return /* @__PURE__ */ jsxs(CompleteThemeProvider, { theme, rowCount: totalRows, children: [
    /* @__PURE__ */ jsx(GlobalBorderStyles, {}),
    /* @__PURE__ */ jsx(ErrorBoundary, { FallbackComponent: ErrorFallback, children: /* @__PURE__ */ jsx(EditorProvider, { children: /* @__PURE__ */ jsx(EditorValueProvider, { children: /* @__PURE__ */ jsx(
      ColumnsProvider,
      {
        columnDiff,
        columnConfig,
        disableColumnMenus,
        tableId,
        isDiffView,
        children: /* @__PURE__ */ jsx(
          FindAndReplaceProvider,
          {
            onSubmit: onFindAndReplace,
            getFindAndReplaceModalProps,
            columnConfig,
            isFiltered,
            children: /* @__PURE__ */ jsx(
              SearchValueProvider,
              {
                onSubmit: onSearchValue,
                columnConfig,
                children: /* @__PURE__ */ jsx(
                  ClipBoardActionsProvider,
                  {
                    tableId,
                    disabled: readOnly,
                    totalRows,
                    additionalRows: additional,
                    tableRef,
                    onDownloadTable,
                    onActionSuccess,
                    children: /* @__PURE__ */ jsxs(
                      KeyActionsProvider,
                      {
                        tableId,
                        disabled: readOnly,
                        totalRows,
                        additionalRows: additional,
                        tableRef,
                        onDownloadTable,
                        children: [
                          /* @__PURE__ */ jsx(ColumnSelectionGroupProvider, { children: table }),
                          /* @__PURE__ */ jsx(TablePortal, {})
                        ]
                      }
                    )
                  }
                )
              }
            )
          }
        )
      }
    ) }) }) })
  ] });
};
Table.displayName = "Table";
named("Table", Table);
export {
  Table as default
};
